// 动态表单使用的公共函数
import { isArray, isNotEmpty } from '@lhb/func';
import { isNotEmptyAny, contrast } from '@lhb/func';
import { ControlType } from '@/common/components/business/DynamicComponent/config';

// 根据 value 获取对应的 ControlType
export const getByValue = (value: any) => {
  return Object.values(ControlType).find((item) => item.value === value);
};

export interface PropertyItem {
  id: number;
  propertyId: number;
  name: string;
  anotherName: string;
  textValue: any;
  [p: string]: any;
}

// 根据 controlType 获取 textValue 的初始值
export const getTextValueInitValue = (controlType: number) => {
  let textValue:any = null;
  // 为空时，赋予默认值
  switch (controlType) {
    case ControlType.SINGLE_RADIO.value:
      textValue = null;
      break;
    case ControlType.CHECK_BOX.value:
      textValue = [];
      break;
    case ControlType.INPUT.value:
    case ControlType.TEXT_AREA.value:
      textValue = null;
      break;
    case ControlType.UPLOAD.value:
      textValue = [];
      break;
    case ControlType.INPUT_NUMBER.value:
    case ControlType.ADDRESS.value:
    case ControlType.AREA.value:
    case ControlType.CUSTOM_AREA.value: // 独立的省市区（不关联商圈）
      textValue = null;
      break;
    default:
      break;
  }
  return textValue;
};

/**
 * 解析 textValue 字符串为对应的格式用于详情展示
 * @param controlType 字段类型
 * @param textValue 字符串格式的 textValue
 * @returns 解析后的 textValue
 */
export const decodeTextValue = (controlType: any, textValue: any) => {
  // 根据字段类型，将字符串的 textValue 进行解析
  if (isNotEmpty(textValue)) {
    // 需不需要加 try？
    try {
      switch (controlType) {
        case ControlType.SINGLE_RADIO.value: {
          const temp = JSON.parse(textValue);
          textValue = { 'id': temp.selectedId, 'name': temp.name, 'input': temp?.input };
          break;
        }
        case ControlType.CHECK_BOX.value: {
          const temp = JSON.parse(textValue);
          textValue = Array.isArray(temp) ? temp.map(item => ({ 'id': item.selectedId, 'name': item.name, 'input': item?.input })) : [];
          break;
        }
        case ControlType.TREE_SELECT.value: {
          const temp = JSON.parse(textValue);
          // 详情、核心信息、子表单和 PDF 的普通文本分支统一展示选项名称。
          textValue = (temp?.optionList || []).map(item => item.name).filter(isNotEmpty).join('、');
          break;
        }
        case ControlType.UPLOAD.value:
          textValue = JSON.parse(textValue);
          break;
        case ControlType.INPUT_NUMBER.value: {
          const temp = JSON.parse(textValue);
          // textValue = contrast(temp, 'value');
          textValue = {
            'value': contrast(temp, 'value'),
            'suffix': contrast(temp, 'suffix'),
          };
          break;
        }
        case ControlType.AREA.value:
        case ControlType.CUSTOM_AREA.value: { // 独立的省市区（不关联商圈）
          const temp = JSON.parse(textValue);
          textValue = [];
          if (temp) {
            temp.provinceId && (textValue[0] = { 'id': temp.provinceId, 'name': temp.provinceName });
            temp.cityId && (textValue[1] = { 'id': temp.cityId, 'name': temp.cityName });
            temp.districtId && (textValue[2] = { 'id': temp.districtId, 'name': temp.districtName });
          }
          break;
        }
        case ControlType.ADDRESS.value: {
          const temp = JSON.parse(textValue);
          textValue = {
            'address': temp.address,
            'lng': temp.longitude,
            'lat': temp.latitude,
            'isCluster': !!temp.isCluster,
          };
          break;
        }
        case ControlType.ESTIMATE_SALES_YHT.value:
        case ControlType.ESTIMATE_SALES_CONCLUSION_YHT.value:
        case ControlType.PASSENGER_FLOW.value:
        case ControlType.POPULATION_FLOW_YHT.value:
        case ControlType.FRANCHISEE_COOPERATION_PROGRESS.value:
        case ControlType.COMPANY_MEMBER.value:
        case ControlType.AI_PREDICT_ENTRY_RATE.value:
        case ControlType.AREA_PROTECTION.value:
        case ControlType.LEASE_TERM.value:
        case ControlType.PROJECT_SPOT_SELECT.value:
        case ControlType.BUSINESS_CONDITION.value:
        {
          textValue = JSON.parse(textValue);
          break;
        }
        default:
          break;
      }
    } catch (error) {
      console.log('error textValue 解析出错了：', textValue);
    }
  } else {
    textValue = getTextValueInitValue(controlType);
  }

  return textValue;
};


/* 递归处理 propertyGroupVOList 为平铺的 json 对象（propertyId 为 key 的对象）
  'propertyConfigVOList': [
    { 'propertyId': 472, 'name': '关联显示的输入框', 'controlType': 2, 'isShow': true, 'textValue': '输入的内容' },
    { 'propertyId': 473, 'name': '测试单选关联', 'controlType': 2, 'isShow': true, 'textValue': 1891 },
  ]
  =>
  {
    472: { 'id': 14675, 'propertyId': 472, 'name': '关联显示的输入框', 'controlType': 2, 'isShow': true, 'textValue': '输入的内容' },
    473: { 'id': 14676, 'propertyId': 473, 'name': '测试单选关联', 'controlType': 2, 'isShow': true, 'textValue': 1891 }
  }
  */
export const dfsPropertyGroupVoList = (propertyGroupVOList: Array<PropertyItem>) => {
  if (!Array.isArray(propertyGroupVOList) || !propertyGroupVOList.length) {
    return null;
  }
  const values = {};
  const tempObj = propertyGroupVOList.reduce((result: any, item: any) => {
    if (item.propertyId) {
      result[item.propertyId] = {
        'id': item.id,
        'propertyId': item.propertyId,
        'name': item.name,
        'anotherName': item.anotherName,
        'controlType': item.controlType,
        'isShow': item.isShow,
        'textValue': item.textValue,
        'templateRestrictionPassiveList': item.templateRestrictionPassiveList,
        'templateRestriction': item.templateRestriction,
        'restriction': item.restriction
      };
    }

    if (Array.isArray(item.childList) && item.childList.length) {
      item.childList.forEach((child: any) => {
        Object.assign(result, dfsPropertyGroupVoList(child.propertyConfigVOList));
      });
    }
    return result;
  }, {});

  Object.assign(values, tempObj);
  return values;
};

// 根据 dfsPropertyGroupVoList 返回的 map 对象判断是否显示该组件
const getItemShow = (item: any, propertyItemMap: any) => {
  /* 'templateRestrictionPassiveList': [
    // 当 propertyId=471 的组件选中 1889 选项时，显示当前组件
    { 'relationsComponent': 471, 'currentValue': [1889] }
  ] */
  const { propertyId, templateRestrictionPassiveList: comovementRelations } = item;

  if (!Array.isArray(comovementRelations) || comovementRelations.length === 0) {
    return true;
  }

  for (const index in comovementRelations) {
    if (true) {
      const { relationsComponent, currentValue } = comovementRelations[index];
      // 当关联的组件或者组件值为空时，组件关联信息异常，不进行校验，直接显示
      if (!isNotEmpty(relationsComponent) || !isNotEmptyAny(currentValue)) {
        console.log('组件关联信息异常，需要调查下，这次让通过', propertyId);
        return true;
      }

      // 关联组件当前的值
      const relationPropertyItem = propertyItemMap[relationsComponent];

      let isMatch = false;
      // 判断是否和 currentValue 匹配
      if (Array.isArray(currentValue) && currentValue.length && isNotEmpty(relationPropertyItem) && relationPropertyItem.textValue) {
        /* relationTextValue 关联组件的值：
          1. 多选-数组，如 [1, 2]、[{ id: 1, name: 'xxx' }, { id: 2, name: 'xxx' }]；
          2. 单选-值，如 {id: 1, name: 'xxx'}
          */
        let relationTextValue: any = null;
        try {
          relationTextValue = JSON.parse(relationPropertyItem.textValue);
        } catch (error) { }

        // 多选-数组
        if (Array.isArray(relationTextValue) && relationTextValue.length) {
          // 如果类型是多选，textValue 是对象数组，需要处理成 ids 数组
          const ids = relationTextValue.map((item: any) => item instanceof Object ? item.selectedId : item);
          isMatch = currentValue.some(val => ids.includes(val));
        } else { // 单选-值
          isMatch = currentValue.includes(relationTextValue instanceof Object ? relationTextValue.selectedId : relationTextValue);
        }
      }

      if (isMatch) {
        return true;
      }
    }

  }

  return false;
};

/* 所有属性的值对象 { propertyId: { textValue: 字段的值, id, propertyId, isShow: 是否显示该组件 } }，如
  {
    472: { 'id': 14675, 'propertyId': 472, 'name': '关联显示的输入框', 'textValue': '输入的内容', isShow: true },
    473: { 'id': 14676, 'propertyId': 473, 'name': '测试单选关联', 'textValue': 1891, isShow: true }
  }
  保存和检查字段关联显示时使用
  */
export const getPropertyItemMap = (propertyGroupVOList: Array<any>) => {
  // 获取所有属性的 propertyId Map
  const propertyItemMap: any = dfsPropertyGroupVoList(propertyGroupVOList);

  if (propertyItemMap && propertyItemMap instanceof Object) {
    // 遍历所有属性，赋予 isShow 字段
    for (const key in propertyItemMap) {
      if (true) {
        const item = propertyItemMap[key];
        item.isShow = getItemShow(item, propertyItemMap);
      }
    }
  }

  return propertyItemMap;
};

/**
 *
 * @param {Object} config 对应templateRestriction
 * @param {Number | String | null} value 值
 * @returns boolean 是否标红
 */
export const checkNumberInputIsRed = (config: any, value: number | string | null) => {
  const { redMark } = config || {};
  if (!redMark) return false;
  const {
    firstComapre,
    firstValue,
    isItemTip,
    isRedMark,
    itemTip,
    joinType,
    secondComapre,
    secondValue,
    // isPageTip
  } = redMark;
  // if (!joinType) return false; // 值为 '&&'或者'||'
  let expressionStr = '';
  if (firstComapre && firstValue) { // 满足的第一个条件
    expressionStr += `(${value}${firstComapre === '=' ? '==' : firstComapre}${firstValue})`;
  }
  if (secondComapre && secondValue) { // 满足的第二个条件
    // 如果第一个条件 ？(拼接上joinType + 第二个条件) : 第二个条件
    expressionStr += `${expressionStr ? `${firstComapre && firstValue && joinType ? joinType : ''}(${value}${secondComapre === '=' ? '==' : secondComapre}${secondValue})` : `${value}${secondComapre}${secondValue}`}`;
  }
  // eslint-disable-next-line no-eval
  if (eval(expressionStr)) return isRedMark && isItemTip && !!itemTip;
  return false;
};

/**
 *
 * @param {Object} config 对应templateRestriction
 * @param {Number | String | null} value 值
 * @returns boolean 是否标红
 */
export const checkRadioIsRed = (config: any, value: number | null) => {
  const { redMark } = config || {};
  if (!(isArray(redMark) && value)) return; // 注意这里预期id不会为0
  const targetConfig = redMark.find((item: any) => item.optionId === value);
  if (targetConfig) {
    const { isRedMark, isItemTip, itemTip } = targetConfig;
    if (isRedMark && isItemTip && !!itemTip) return targetConfig;
    return null;
  }
  return null;
};

/**
 *
 * @param {Object} config 对应templateRestriction
 * @param {Number | String | null} value 值
 * @returns boolean 是否标红
 */
export const checkboxCheckIsRed = (config: any, value: number[]) => {
  const { redMark } = config || {};
  if (!(isArray(redMark) && isArray(value))) return [];
  const option: any[] = [];
  value.forEach((item: any) => {
    redMark.forEach((redMarkItem: any) => {
      const { optionId, isRedMark, isItemTip, itemTip } = redMarkItem;
      if (optionId !== item.id) return;
      isRedMark && isItemTip && itemTip && (option.push(redMarkItem));
    });
  });
  return option;
};
